import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  RefreshCw, Loader2, ArrowLeft, Filter, CheckCircle2,
  FileText, FileQuestion, ClipboardList, FileImage, Truck,
  Bell, AlertTriangle, Scale, CheckSquare, FileCheck,
  Users, Clock, ChevronRight, AlertCircle, ArrowRight,
  Send, UserCheck, CalendarClock, Timer
} from 'lucide-react';

// Process step component for the visual flow
const ProcessStep = ({ label, value, date, isActive, isCompleted }) => (
  <div className="flex flex-col items-center text-center min-w-0 flex-1">
    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 truncate w-full">{label}</span>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
      isCompleted ? 'bg-green-500 border-green-500 text-white' :
      isActive ? 'bg-blue-500 border-blue-500 text-white' :
      'bg-white border-slate-200 text-slate-400'
    }`}>
      {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : 
       isActive ? <div className="w-2 h-2 rounded-full bg-white" /> :
       <div className="w-2 h-2 rounded-full bg-slate-300" />}
    </div>
    <span className="text-[11px] font-medium text-slate-700 mt-1 truncate w-full max-w-[100px]">{value || '—'}</span>
    {date && <span className="text-[10px] text-slate-400">{date}</span>}
  </div>
);

// Arrow connector between process steps
const StepArrow = () => (
  <div className="flex items-center justify-center px-1 pt-5">
    <ArrowRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
  </div>
);

// Days remaining indicator
const DaysIndicator = ({ daysLeft, priority }) => {
  if (daysLeft === null || daysLeft === undefined) return null;
  
  const isOverdue = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 3;
  const isWarning = daysLeft > 3 && daysLeft <= 7;
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
      isOverdue ? 'bg-red-100 text-red-700' :
      isUrgent ? 'bg-amber-100 text-amber-700' :
      isWarning ? 'bg-yellow-50 text-yellow-700' :
      'bg-slate-100 text-slate-600'
    }`} data-testid="days-indicator">
      <Timer className="h-3 w-3" />
      {isOverdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
    </div>
  );
};

export default function AwaitingItemsTab({
  awaitingItems,
  awaitingLoading,
  awaitingModuleFilter,
  setAwaitingModuleFilter,
  awaitingPriorityFilter,
  setAwaitingPriorityFilter,
  setAwaitingRefreshKey,
  setActiveTab
}) {
  const navigate = useNavigate();

  const moduleLabels = {
    site_instruction: { label: 'Site Instructions', icon: FileText, bgClass: 'bg-blue-50 border-blue-100', textClass: 'text-blue-800', badgeClass: 'bg-blue-200 text-blue-800', iconClass: 'text-blue-600' },
    rfi: { label: 'RFIs', icon: FileQuestion, bgClass: 'bg-purple-50 border-purple-100', textClass: 'text-purple-800', badgeClass: 'bg-purple-200 text-purple-800', iconClass: 'text-purple-600' },
    irs: { label: 'Information Required', icon: ClipboardList, bgClass: 'bg-violet-50 border-violet-100', textClass: 'text-violet-800', badgeClass: 'bg-violet-200 text-violet-800', iconClass: 'text-violet-600' },
    shop_drawing: { label: 'Shop Drawings', icon: FileImage, bgClass: 'bg-orange-50 border-orange-100', textClass: 'text-orange-800', badgeClass: 'bg-orange-200 text-orange-800', iconClass: 'text-orange-600' },
    procurement: { label: 'Procurement', icon: Truck, bgClass: 'bg-amber-50 border-amber-100', textClass: 'text-amber-800', badgeClass: 'bg-amber-200 text-amber-800', iconClass: 'text-amber-600' },
    notice: { label: 'Notices', icon: Bell, bgClass: 'bg-yellow-50 border-yellow-100', textClass: 'text-yellow-800', badgeClass: 'bg-yellow-200 text-yellow-800', iconClass: 'text-yellow-600' },
    delay_event: { label: 'Delay Events', icon: AlertTriangle, bgClass: 'bg-red-50 border-red-100', textClass: 'text-red-800', badgeClass: 'bg-red-200 text-red-800', iconClass: 'text-red-600' },
    claim: { label: 'Claims', icon: Scale, bgClass: 'bg-emerald-50 border-emerald-100', textClass: 'text-emerald-800', badgeClass: 'bg-emerald-200 text-emerald-800', iconClass: 'text-emerald-600' },
    task: { label: 'Tasks', icon: CheckSquare, bgClass: 'bg-green-50 border-green-100', textClass: 'text-green-800', badgeClass: 'bg-green-200 text-green-800', iconClass: 'text-green-600' },
    meeting_action: { label: 'Meeting Actions', icon: FileCheck, bgClass: 'bg-cyan-50 border-cyan-100', textClass: 'text-cyan-800', badgeClass: 'bg-cyan-200 text-cyan-800', iconClass: 'text-cyan-600' }
  };

  const moduleOrder = ['site_instruction', 'rfi', 'irs', 'shop_drawing', 'procurement', 'notice', 'delay_event', 'claim', 'task', 'meeting_action'];

  const filteredItems = awaitingItems.filter(item => {
    const matchesModule = awaitingModuleFilter === 'all' || item.type === awaitingModuleFilter;
    const matchesPriority = awaitingPriorityFilter === 'all' || item.priority === awaitingPriorityFilter;
    return matchesModule && matchesPriority;
  });

  const groupedItems = {};
  filteredItems.forEach(item => {
    if (!groupedItems[item.type]) groupedItems[item.type] = [];
    groupedItems[item.type].push(item);
  });

  const filterButtons = [
    { value: 'all', label: 'All', count: awaitingItems.length },
    { value: 'site_instruction', label: 'Site Inst.', count: awaitingItems.filter(i => i.type === 'site_instruction').length, color: 'blue' },
    { value: 'rfi', label: 'RFIs', count: awaitingItems.filter(i => i.type === 'rfi').length, color: 'purple' },
    { value: 'irs', label: 'Info Required', count: awaitingItems.filter(i => i.type === 'irs').length, color: 'violet' },
    { value: 'shop_drawing', label: 'Shop Drgs', count: awaitingItems.filter(i => i.type === 'shop_drawing').length, color: 'orange' },
    { value: 'procurement', label: 'Procurement', count: awaitingItems.filter(i => i.type === 'procurement').length, color: 'amber' },
    { value: 'notice', label: 'Notices', count: awaitingItems.filter(i => i.type === 'notice').length, color: 'yellow' },
    { value: 'delay_event', label: 'Delay Events', count: awaitingItems.filter(i => i.type === 'delay_event').length, color: 'red' },
    { value: 'claim', label: 'Claims', count: awaitingItems.filter(i => i.type === 'claim').length, color: 'emerald' },
    { value: 'task', label: 'Tasks', count: awaitingItems.filter(i => i.type === 'task').length, color: 'green' },
    { value: 'meeting_action', label: 'Actions', count: awaitingItems.filter(i => i.type === 'meeting_action').length, color: 'cyan' }
  ].filter(f => f.count > 0 || f.value === 'all');

  const summaryStats = [
    { label: 'Total', count: awaitingItems.length, numClass: 'text-slate-600', borderClass: 'border-slate-200' },
    { label: 'Overdue', count: awaitingItems.filter(i => i.priority === 'overdue').length, numClass: 'text-red-600', borderClass: 'border-red-200' },
    { label: 'Critical', count: awaitingItems.filter(i => i.priority === 'critical').length, numClass: 'text-amber-600', borderClass: 'border-amber-200' },
    { label: 'Info Required', count: awaitingItems.filter(i => i.type === 'irs').length, numClass: 'text-violet-600', borderClass: 'border-violet-200' },
    { label: 'Shop Drawings', count: awaitingItems.filter(i => i.type === 'shop_drawing').length, numClass: 'text-orange-600', borderClass: 'border-orange-200' },
    { label: 'Procurement', count: awaitingItems.filter(i => i.type === 'procurement').length, numClass: 'text-amber-600', borderClass: 'border-amber-200' }
  ];

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }); }
    catch { return null; }
  };

  // Get process steps for each module type
  const getProcessSteps = (item) => {
    const created = fmtDate(item.createdAt);
    const due = fmtDate(item.dueDate);
    const color = moduleLabels[item.type]?.color || 'slate';

    switch (item.type) {
      case 'site_instruction':
        return [
          { label: 'Issued', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Status', value: item.status, isActive: true, color },
          { label: 'Response By', value: item.toWhom, date: due }
        ];
      case 'rfi':
        return [
          { label: 'Raised By', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Addressed To', value: item.toWhom, isActive: true, color },
          { label: 'Response Due', value: due || 'No deadline', isActive: false }
        ];
      case 'shop_drawing':
        return [
          { label: 'Submitted By', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Reviewing', value: item.toWhom, isActive: true, color },
          { label: 'Required On Site', value: due || 'TBD', isActive: false }
        ];
      case 'notice':
        return [
          { label: 'Issued By', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Status', value: item.status, isActive: true, color },
          { label: 'Response Due', value: item.toWhom, date: due }
        ];
      case 'delay_event':
        return [
          { label: 'Reported By', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Status', value: item.status, isActive: true, color },
          { label: 'Action By', value: item.toWhom, date: due }
        ];
      case 'claim':
        return [
          { label: 'Claimant', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Status', value: item.status, isActive: true, color },
          { label: 'Decision By', value: item.toWhom, date: due }
        ];
      case 'task':
        return [
          { label: 'Created By', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Assigned To', value: item.toWhom, isActive: true, color },
          { label: 'Due Date', value: due || 'No deadline', isActive: false }
        ];
      case 'irs':
        return [
          { label: 'Required From', value: item.toWhom, isActive: true, color },
          { label: 'Status', value: item.status, isActive: item.status === 'Late', color: item.status === 'Late' ? 'red' : color },
          { label: 'Info Required By', value: due || 'TBD', date: null }
        ];
      case 'procurement':
        return [
          { label: 'Managed By', value: item.submittedBy, isCompleted: true },
          { label: 'Supplier', value: item.toWhom, isActive: true, color },
          { label: 'Required Date', value: due || 'TBD', isActive: false }
        ];
      case 'meeting_action':
        return [
          { label: 'From Meeting', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Assigned To', value: item.toWhom, isActive: true, color },
          { label: 'Due Date', value: due || 'No deadline', isActive: false }
        ];
      default:
        return [
          { label: 'Submitted', value: item.submittedBy, date: created, isCompleted: true },
          { label: 'Status', value: item.status, isActive: true, color },
          { label: 'Due', value: due || 'TBD', isActive: false }
        ];
    }
  };

  return (
    <div className="space-y-4">
      {/* Back to Project */}
      <div className="mb-2">
        <Button variant="ghost" size="sm" onClick={() => setActiveTab('overview')} className="text-slate-600 hover:text-slate-900" data-testid="back-to-project-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Project
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Items Requiring Attention</h2>
          <p className="text-sm text-slate-500">Track the full process flow — who submitted, current status, and response deadlines</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAwaitingRefreshKey(prev => prev + 1)} disabled={awaitingLoading} className="rounded-sm" data-testid="awaiting-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${awaitingLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex flex-wrap gap-2">
          {filterButtons.map((filter) => (
            <Button key={filter.value} variant={awaitingModuleFilter === filter.value ? 'default' : 'outline'} size="sm"
              onClick={() => setAwaitingModuleFilter(filter.value)}
              className={`rounded-sm ${awaitingModuleFilter === filter.value ? 'bg-slate-900 text-white' : ''}`}
              data-testid={`awaiting-filter-${filter.value}`}
            >
              {filter.label}
              <Badge className={`ml-2 ${awaitingModuleFilter === filter.value ? 'bg-white text-slate-900' : 'bg-slate-200 text-slate-700'}`}>
                {filter.count}
              </Badge>
            </Button>
          ))}
        </div>
        <Select value={awaitingPriorityFilter} onValueChange={setAwaitingPriorityFilter}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="awaiting-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {summaryStats.map((stat) => (
          <Card key={stat.label} className={`rounded-sm ${stat.borderClass}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${stat.numClass}`}>{stat.count}</p>
              <p className="text-xs text-slate-500 uppercase">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Items List */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {awaitingLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading items...</span>
            </div>
          ) : awaitingItems.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-16 w-16 text-green-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">All caught up!</h3>
              <p className="text-slate-500">No items requiring attention at this time.</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No items match filters</h3>
              <p className="text-slate-500">Try adjusting your filter criteria.</p>
            </div>
          ) : (
            <div>
              {moduleOrder.filter(type => groupedItems[type]?.length > 0).map(moduleType => {
                const config = moduleLabels[moduleType];
                const ModuleIcon = config.icon;
                const items = groupedItems[moduleType];
                
                return (
                  <div key={moduleType} className="border-b border-slate-200 last:border-b-0">
                    {/* Module Header */}
                    <div className={`px-4 py-3 ${config.bgClass} flex items-center gap-3 border-b`}>
                      <ModuleIcon className={`h-5 w-5 ${config.iconClass}`} />
                      <span className={`font-semibold ${config.textClass}`}>{config.label}</span>
                      <Badge className={config.badgeClass}>{items.length}</Badge>
                    </div>
                    
                    {/* Module Items */}
                    <div className="divide-y divide-slate-100">
                      {items.map((item) => {
                        const steps = getProcessSteps(item);
                        
                        return (
                          <div 
                            key={`${item.type}-${item.id}`}
                            className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                              item.priority === 'overdue' ? 'bg-red-50/60 hover:bg-red-50' :
                              item.priority === 'critical' ? 'bg-amber-50/60 hover:bg-amber-50' : ''
                            }`}
                            onClick={() => navigate(item.url)}
                            data-testid={`awaiting-item-${item.id}`}
                          >
                            {/* Top row: badges + title + days indicator */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Badge className={`rounded-sm text-xs ${
                                    item.priority === 'overdue' ? 'bg-red-600 text-white' :
                                    item.priority === 'critical' ? 'bg-amber-500 text-white' :
                                    'bg-slate-200 text-slate-700'
                                  }`} data-testid="item-status-badge">
                                    {item.status}
                                  </Badge>
                                  {item.priority === 'overdue' && (
                                    <Badge className="rounded-sm text-xs bg-red-100 text-red-700">
                                      <AlertCircle className="h-3 w-3 mr-1" /> Overdue
                                    </Badge>
                                  )}
                                  {item.priority === 'critical' && (
                                    <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700">
                                      <AlertTriangle className="h-3 w-3 mr-1" /> Critical
                                    </Badge>
                                  )}
                                  <DaysIndicator daysLeft={item.daysLeft} priority={item.priority} />
                                </div>
                                <h4 className="font-semibold text-slate-900 truncate text-sm">{item.title}</h4>
                                {item.description && (
                                  <p className="text-xs text-slate-500 truncate">{item.description}</p>
                                )}
                              </div>
                              <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0 mt-1" />
                            </div>

                            {/* Process Flow - Visual arrows */}
                            <div className="flex items-start justify-between bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid="process-flow">
                              {steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                  {idx > 0 && <StepArrow />}
                                  <ProcessStep {...step} />
                                </React.Fragment>
                              ))}
                            </div>

                            {/* Bottom row: submitted by + to whom (mobile-friendly text) */}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                              {item.submittedBy && (
                                <span className="flex items-center gap-1" data-testid="submitted-by">
                                  <Send className="h-3 w-3 text-slate-400" />
                                  <span className="text-slate-400">From:</span> <span className="font-medium text-slate-600">{item.submittedBy}</span>
                                </span>
                              )}
                              {item.toWhom && (
                                <span className="flex items-center gap-1" data-testid="to-whom">
                                  <UserCheck className="h-3 w-3 text-slate-400" />
                                  <span className="text-slate-400">To:</span> <span className="font-medium text-slate-600">{item.toWhom}</span>
                                </span>
                              )}
                              {item.createdAt && (
                                <span className="flex items-center gap-1">
                                  <CalendarClock className="h-3 w-3 text-slate-400" />
                                  <span className="text-slate-400">Created:</span> {fmtDate(item.createdAt)}
                                </span>
                              )}
                              {item.dueDate && (
                                <span className={`flex items-center gap-1 ${item.priority === 'overdue' ? 'text-red-600 font-medium' : ''}`}>
                                  <Clock className="h-3 w-3" />
                                  <span className="text-slate-400">Due:</span> {fmtDate(item.dueDate)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
